/**
 * O que faz: agrupa retornos programados em faixas de agenda considerando o dia civil de São Paulo.
 * Onde usa: listas e contadores da feature de retornos programados.
 * Conexões: contratos de LeadReturn.
 * Tabelas: Nenhuma; recebe dados já carregados da tabela lead_returns.
 * Data: 24/08/2026
 */

import type { LeadReturn } from './returnTypes';

export interface ReturnGroups {
  overdue: LeadReturn[];
  today: LeadReturn[];
  nextSevenDays: LeadReturn[];
  later: LeadReturn[];
}

const SAO_PAULO_TIME_ZONE = 'America/Sao_Paulo';

const dayKeyFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: SAO_PAULO_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

const buildSaoPauloDayKey = (value: Date | string) => {
  // Motivo: o agrupamento depende do dia civil em São Paulo, não do dia UTC salvo no banco.
  const parsedDate = typeof value === 'string' ? new Date(value) : value;

  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  const parts = dayKeyFormatter.formatToParts(parsedDate);
  const year = parts.find(part => part.type === 'year')?.value;
  const month = parts.find(part => part.type === 'month')?.value;
  const day = parts.find(part => part.type === 'day')?.value;

  if (!year || !month || !day) {
    return null;
  }

  return `${year}-${month}-${day}`;
};

const buildDayIndex = (dayKey: string) => {
  // Motivo: transformar a chave yyyy-MM-dd em número permite comparar janelas sem depender do fuso do navegador.
  const [year, month, day] = dayKey.split('-').map(Number);
  return Math.floor(Date.UTC(year, month - 1, day) / 86400000);
};

const sortReturns = (items: LeadReturn[]) => {
  // Motivo: a UI precisa de ordem determinística para não "pular" itens entre renders equivalentes.
  return [...items].sort((left, right) => {
    const scheduledComparison = left.scheduled_for.localeCompare(right.scheduled_for);

    if (scheduledComparison !== 0) {
      return scheduledComparison;
    }

    return left.id.localeCompare(right.id);
  });
};

export function groupReturnsByDay(items: LeadReturn[], now: Date): ReturnGroups {
  const todayKey = buildSaoPauloDayKey(now);

  if (!todayKey) {
    throw new Error('Data de referência inválida para agrupar os retornos.');
  }

  const todayIndex = buildDayIndex(todayKey);

  const groups: ReturnGroups = {
    overdue: [],
    today: [],
    nextSevenDays: [],
    later: [],
  };

  for (const item of items) {
    // Motivo: cada retorno precisa ser classificado pela data da agenda em São Paulo para evitar off-by-one perto da meia-noite UTC.
    const scheduledDayKey = buildSaoPauloDayKey(item.scheduled_for);

    if (!scheduledDayKey) {
      // Motivo: um timestamp corrompido não pode derrubar a tela; ele vai para "later" para continuar visível e revisável.
      groups.later.push(item);
      continue;
    }

    const scheduledDayIndex = buildDayIndex(scheduledDayKey);

    if (scheduledDayIndex < todayIndex) {
      groups.overdue.push(item);
      continue;
    }

    if (scheduledDayIndex === todayIndex) {
      groups.today.push(item);
      continue;
    }

    if (scheduledDayIndex - todayIndex <= 7) {
      groups.nextSevenDays.push(item);
      continue;
    }

    groups.later.push(item);
  }

  return {
    overdue: sortReturns(groups.overdue),
    today: sortReturns(groups.today),
    nextSevenDays: sortReturns(groups.nextSevenDays),
    later: sortReturns(groups.later),
  };
}
